import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

export default function AdminArticles() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100, status: "" };
      if (filter) params.category = filter;
      const { data } = await api.get("/articles", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-ink mb-2">Content</div>
          <h1 className="heading-serif text-4xl text-ink">{t.admin.articles}</h1>
        </div>
        <Link
          to="/admin/articles/new"
          data-testid="admin-new-article-btn-2"
          className="bg-ink text-white h-11 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 inline-flex items-center"
        >
          {t.admin.new_article}
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["", "war_crime", "terror_crime", "monitoring", "study", "analysis", "news", "director_message", "publication"].map((c) => (
          <button
            key={c || "all"}
            onClick={() => setFilter(c)}
            data-testid={`admin-filter-${c || "all"}`}
            className={`px-3 h-9 text-[12px] uppercase tracking-widest border ${filter === c ? "bg-ink text-white border-ink" : "border-slate200 text-slate600 hover:border-ink hover:text-ink"}`}
          >
            {c ? (t.categories[c] || c) : t.filters.all}
          </button>
        ))}
      </div>

      <div className="border border-slate200 bg-white">
        <div className="grid grid-cols-[1fr_140px_120px_120px_100px] px-5 h-10 items-center text-[11px] uppercase tracking-widest text-slate500 border-b border-slate200">
          <div>Title</div>
          <div>Category</div>
          <div>Status</div>
          <div>Date</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate500">{t.common.loading}</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate500" data-testid="admin-articles-empty">{t.common.no_results}</div>
        ) : (
          items.map((a) => {
            const tr = a.translations?.[a.source_lang] || a.translations?.en || a.translations?.ar || a.translations?.fr || {};
            return (
              <div
                key={a.id}
                className="grid grid-cols-[1fr_140px_120px_120px_100px] px-5 py-3 items-center text-[13px] border-b border-slate200 last:border-0"
                data-testid={`admin-article-row-${a.slug}`}
              >
                <div className="truncate pr-3">
                  <div className="text-ink font-medium truncate">{tr.title || a.slug}</div>
                  <div className="text-[11px] text-slate500 mt-0.5">/{a.slug}</div>
                </div>
                <div className="text-slate600 text-[12px]">{t.categories[a.category] || a.category}</div>
                <div>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${a.status === "published" ? "bg-ink text-white" : "bg-slate200 text-slate700"}`}>
                    {a.status === "published" ? t.admin.published_status : t.admin.draft}
                  </span>
                </div>
                <div className="text-[12px] text-slate500">{new Date(a.publish_date).toLocaleDateString()}</div>
                <div className="flex items-center justify-end gap-2">
                  <Link to={`/admin/articles/${a.id}`} data-testid={`admin-edit-${a.slug}`} className="p-1.5 border border-slate200 hover:border-ink"><Pencil className="w-3.5 h-3.5" /></Link>
                  <button onClick={() => remove(a.id)} data-testid={`admin-delete-${a.slug}`} className="p-1.5 border border-slate200 hover:border-crimson hover:text-crimson"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
